import tokens from "../../../../design-system/tokens.js";
import VideoEmbed from "../../../../design-system/components/Video/Video.jsx";
import styles from "../../../../design-system/components/PopOver/popOver.css.js";

/**
 * An array containing step-by-step data utilized for guiding users through a tour in the application.
 *
 * Each step specifies a title, content to display, target elements for highlighting, placement of the instructions,
 * and additional data for controlling the navigation and state of the tour.
 *
 * Properties:
 * - `title`: The headline or brief description of the step.
 * - `content`: The main instructional content to be displayed to the user during the step. It may include JSX elements for styled or interactive content.
 * - `target`: The specific DOM element that should be highlighted during the step.
 * - `placement`: Indicates the position of the tooltip or instruction display relative to the target element (e.g., 'top', 'bottom', 'center').
 * - `data`: Additional data for controlling the step, such as the path for the next step or other contextual metadata.
 * - `isFixed`: Optional boolean to indicate if the tooltip or content for this step should remain fixed in position (doesn't move during scrolling).
 * - `locale`: Optional object for step-specific locale overrides, such as custom text for buttons or instructions.
 */
export const stepsDataForUserTour = [
  //Lernreise
  {
    title: "Geschafft, deine Lernreise ist bereit!",
    content: (
      <>
        <p style={{ marginBottom: tokens.space["3x"] }}>
          Schaue dir die Digital Sparks Lernreise an, bereite alles vor und lade
          sobald du soweit bist, deine Schüler*innen ein!
        </p>
        <VideoEmbed url="https://vimeo.com/1060441465?share=copy" />
      </>
    ),
    target: "#title",
    placement: "center",
    locale: {
      next: <p>Jetzt loslegen!</p>,
    },
    showSkipButton: false,
    styles: {
      buttonNext: styles.buttonOnly,
    },
  },
  {
    title: "Lernreise (Zeitplan)",
    content: (
      <>
        Willkommen in deiner Lernreise. Diese Übersicht, wir nennen sie auch
        „Zeitplan“, zeigt dir, wie die Lernreise aufgebaut ist.{" "}
        <b>Deine Schüler*innen sehen diesen Zeitplan genau wie du.</b> Es gibt
        aber auch Unterschiede, dazu kommen wir später.
      </>
    ),
    target: "#title",
    placement: "auto",
    isFixed: true,
  },
  {
    title: "Phasen",
    content:
      "Hier siehst du die Aufteilung der Etappen der Lernreise in die verschiedenen Phasen: Discover, Create und Share. Jeder Spark folgt diesen Phasen.",
    target: "#higher-level-navigation",
    placement: "right",
    isFixed: true,
  },
  {
    title: "Etappen",
    content:
      "Hier kannst du durch die verschiedenen Etappen deiner Lernreise navigieren.",
    target: "#chapter-navigation",
    placement: "right",
    isFixed: true,
  },
  {
    title: "Stationen",
    content:
      "In den verschiedenen Stationen einer Etappe finden sich chronologisch aufeinander aufbauende Lerninhalte und Aufgaben.",
    target: "#chapter-schedule",
    placement: "left-start",
    isFixed: true,
    data: {
      next: (slug) => `step/${slug}`,
    },
  },
  //Slides
  {
    title: "Inhalte",
    content:
      "Über Texte, Videos, Illustrationen, Aufgaben, Links und interaktive Inhalte gewinnen deine Schüler*innen vielfältige Perspektiven auf das Thema.",
    target: "#page",
    placement: "center",
    isFixed: true,
    data: {
      prev: () => -1,
    },
  },
  {
    title: "Navigation",
    content: "Mit den Pfeilen klickst du dich durch die Inhalte der Stationen.",
    target: "#step-navigation",
    placement: "bottom",
    isFixed: true,
  },
  {
    title: "Zurück zur Übersicht",
    content: "Über den „Schließen“-Button gelangst du zum Zeitplan zurück.",
    target: "#close-button",
    placement: "left-start",
    isFixed: true,
    data: {
      next: () => -1,
    },
  },
  //Wieder zurück zur Lernreise
  {
    title: "Toolbar",
    content: (
      <>
        Deine Toolbar zeigt dir alle wichtigen Funktionen deiner Lernreise.
        <b>
          {" "}
          Wichtig: Deine Schüler*innen sehen eine etwas andere Toolbar - sie
          können z.B. nicht Personen zur Lernreise einladen oder Einstellungen
          vornehmen.
        </b>
      </>
    ),
    target: "#toolbar",
    placement: "auto",
    isFixed: true,
    data: {
      prev: (slug) => `step/${slug}`,
    },
  },
  {
    title: "Einladen",
    content: (
      <>
        Hier kannst du Gruppen bilden und deine Schüler*innen einladen. Außerdem
        kannst du deine Kolleg*innen einladen. Sie können den Spark mit dir
        anleiten.
      </>
    ),
    target: "#group-button",
    placement: "top",
    isFixed: true,
  },
  {
    // Only rendered when the spark has the glossary active — the Klassenzimmer
    // button is gated on the same flag, so WorkshopUserTour filters this step
    // out otherwise (its target would never mount).
    title: "Klassenzimmer",
    content: (
      <>
        Mit dem Klassenzimmer Feature kannst du vorsichtig Einfluss auf die App
        deiner Schüler*innen nehmen. Pausiere alle Bildschirme oder sende eine
        Nachricht an deine Klasse.
      </>
    ),
    target: "#classroom-button",
    placement: "top",
    isFixed: true,
  },
  {
    title: "Werkzeuge",
    content: (
      <>
        Hier findest du mit Lernreise, Bibliothek und Showroom die wichtigsten
        inhaltlichen Werkzeuge des Sparks. Die Lernreise haben wir bereits
        besprochen.
      </>
    ),
    target: "#basic-tools",
    placement: "top",
    isFixed: true,
  },
  {
    title: "Bibliothek & Showroom",
    content: (
      <>
        In der<b> Bibliothek </b> sind kuratierte Inhalte zum Thema hinterlegt.
        Deine Schüler*innen werden während des Sparks dazu motiviert, diese zum
        Stöbern und für die Recherche zu nutzen. Der<b> Showroom </b>ermöglicht
        am Ende des Sparks das Teilen und Diskutieren der Projekte.
      </>
    ),
    target: "#basic-tools",
    placement: "top",
    isFixed: true,
  },
  {
    title: "Handbuch",
    content:
      "In deinem Handbuch erhältst du Hilfestellung, um deine Lernreise vorzubereiten, Antworten zu häufig gestellten Fragen, Videoerklärungen und einen Spickzettel zum Ausdrucken, den du während der Durchführung mitnehmen kannst.",
    target: "#handbook-button",
    placement: "top",
    isFixed: true,
  },
  {
    title: "Einführung erneut anschauen?",
    content:
      "Wenn du dir die Einführung später erneut anschauen magst, findest du sie in den Optionen.",
    target: "#options-button",
    placement: "top",
    isFixed: true,
  },
  {
    title: "Geschafft, lege los sobald du soweit bist.",
    content: (
      <>
        Lade deine Schüler*innen ein, lies ein wenig im Handbuch oder mache dich
        noch weiter mit den Inhalten vertraut, indem du dich durch den Zeitplan
        klickst. <b> Schön, dass du da bist :)</b>
      </>
    ),
    target: "#handbook-button",
    placement: "center",
    isFixed: true,
  },
];
